import { Characters } from '../../common/helpers/unicode.helper';
import { Modal } from '../../cdl/Modal/Modal';
import { formatCompanyName } from '../../common/helpers/formatCompanyName.helper';
import { formatMoney } from '../../common/helpers/formatMoney.helper';
import { formatNumber } from '../../common/helpers/formatNumber.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { IconExchange } from '../../common/icons/cdl/Exchange';
import { Box } from '../../common/ui/Box';
import { Text } from '../../cdl/Text/Text';
import { TableBuilder } from '../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../cdl/TableBuilder/TableBuilderColumn';
import { PriceHeading } from '../../common/FormikProductTable/common/PriceHeading';
import { formatPrice } from '../../common/helpers/formatPrice.helper';

const ProductCell = ({ item }) => {
    return (
        <Box display="flex" alignItems="center">
            {item.translated ? (
                <IconExchange
                    style={{
                        marginRight: '4px',
                    }}
                />
            ) : null}
            {item.product?.name
                ? item.product.name
                : `${translate('compare.itemsPopup.requested')} ${item.baseProduct.name}`}
        </Box>
    );
};

export const ItemsPopup = ({ price, onDismiss, open }) => {
    const role = useRole();

    const formatVolume = (volume) =>
        `${formatNumber({
            number: volume,
        })}L`;

    const hasPartialPricing = price.partialTotalPrice && !price.totalPrice;
    const pricedItems = price.items.filter((item) => item.price);
    const unpricedItems = price.items.filter((item) => !item.price);

    const tableData = [
        // Items with prices
        ...pricedItems.map((item) => ({
            type: 'item',
            item,
            price: item.price,
            volume: item.volume,
            total: item.total,
            translated: item.translated,
        })),
        // Subtotal row (only when there are unpriced items)
        ...(hasPartialPricing
            ? [
                  {
                      product: translate('compare.itemsPopup.subtotal'),
                      price: price.partialPpl,
                      volume: price.partialVolume,
                      total: price.partialTotalPrice,
                      isBold: true,
                  },
              ]
            : []),
        // Items without prices
        ...unpricedItems.map((item) => ({
            type: 'item',
            item,
            price: null,
            volume: item.volume,
            total: null,
            translated: item.translated,
        })),
        // Total row
        {
            product: translate('compare.itemsPopup.total'),
            price: price.ppl || null,
            volume: price.volume,
            total: price.totalPrice,
            isBold: true,
        },
    ];

    return (
        <Modal size="large" onDismiss={onDismiss} isOpen={open} scrollable={false}>
            <Box padding={24}>
                <Text variant="title" as="h2">
                    {formatCompanyName({
                        company: price.supplier,
                    })}
                </Text>

                <TableBuilder data={tableData} alternateRowColors={false} grow={false}>
                    <TableBuilderColumn header={translate('compare.itemsPopup.product')}>
                        {(row) => {
                            if (row.type === 'item') {
                                return <ProductCell item={row.item} />;
                            }
                            return <Text weight={row.isBold ? 'semi' : 'normal'}>{row.product}</Text>;
                        }}
                    </TableBuilderColumn>
                    <TableBuilderColumn header={<PriceHeading align="right" />} numeric>
                        {(row) => {
                            const content = row.price ? formatPrice(row.price) : Characters.EN_DASH;
                            return <Text weight={row.isBold ? 'semi' : 'normal'}>{content}</Text>;
                        }}
                    </TableBuilderColumn>
                    <TableBuilderColumn header={translate('compare.itemsPopup.volume')} numeric>
                        {(row) => {
                            const content = formatVolume(row.volume);
                            return <Text weight={row.isBold ? 'semi' : 'normal'}>{content}</Text>;
                        }}
                    </TableBuilderColumn>
                    <TableBuilderColumn header={translate('compare.itemsPopup.total')} numeric>
                        {(row) => {
                            let content;
                            if (row.total) {
                                content = formatMoney({
                                    value: row.total.value,
                                    currency: row.total.currency,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4,
                                });
                            } else {
                                content = Characters.EN_DASH;
                            }
                            return <Text weight={row.isBold ? 'semi' : 'normal'}>{content}</Text>;
                        }}
                    </TableBuilderColumn>
                </TableBuilder>
            </Box>
        </Modal>
    );
};
